import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../context/AuthContext';
import { fetchUpcomingEvents, type CalendarEventItem } from '../../../lib/calendarApi';

function fmt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ro-RO', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function AppointmentsCard() {
  const navigate = useNavigate();
  const { me } = useAuth();

  const [items, setItems] = useState<CalendarEventItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchUpcomingEvents(6);
        if (!cancelled) setItems(data);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    const t = window.setInterval(() => void load(), 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, []);

  const myId = Number(me?.id);
  const mine = useMemo(() => {
    return items
      .map((ev) => {
        const p = Array.isArray(ev.participants) ? ev.participants.find((x) => Number(x.user_id) === myId) : null;
        return { ev, me: p || null };
      })
      .filter((x) => Boolean(x.me));
  }, [items, myId]);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="CalendarDays" size={20} className="text-muted-foreground" />
          <h2 className="text-lg font-semibold text-foreground">Programări</h2>
        </div>
        <Button variant="ghost" size="sm" iconName="ExternalLink" onClick={() => navigate('/calendar')}>
          Vezi calendar
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Se incarca...</div>
      ) : mine.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          Nu exista programari viitoare pentru tine.
          <div className="mt-2 text-xs">
            Daca ai sub 16 ani, invitatia vine catre parinti.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {mine.map(({ ev, me: part }) => {
            const response = ((part as any)?.response || (part?.approved ? 'accepted' : 'pending')) as string;
            const badge =
              response === 'pending'
                ? { text: 'În așteptare', cls: 'bg-warning/20 text-warning border-warning/30' }
                : response === 'declined'
                  ? { text: 'Respins', cls: 'bg-error/10 text-error border-error/20' }
                  : { text: 'Acceptat', cls: 'bg-success/15 text-success border-success/25' };
            return (
            <div key={ev.id} className="flex items-start gap-3 p-3 bg-muted/30 rounded-md">
              <div className="flex-shrink-0 mt-0.5">
                <Icon name="Clock" size={16} className="text-muted-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-foreground truncate">{ev.title}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {fmt(ev.start_time)} → {fmt(ev.end_time)}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="text-xs px-2 py-0.5 rounded bg-muted text-foreground whitespace-nowrap">
                  {ev.event_type || 'Altul'}
                </span>
                <span className={`text-[11px] px-2 py-0.5 rounded border ${badge.cls}`}>{badge.text}</span>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
